import { useQuery } from '@tanstack/react-query'
import { fetchApacheEChartData } from '../../services/entityServices'
import { ISelectedAttributes } from '../../components/ViewEntity/interfaces'
import { IApacheChartAPIResponse } from '../../services/apiInterfaces'
import { useAppSelector } from '../../redux/hook'
import dayjs from 'dayjs'
import { ISO_DATE_TIME_FORMAT } from '../../utils/DateAndTimeUtils'

export const useGetApacheEchartData = (
    viewID: string,
    selectedAttributes: ISelectedAttributes[],
    startDate: string,
    endDate: string,
    orgId: string,
    clampToRelationStart?: boolean,
): ReturnType<typeof useQuery<IApacheChartAPIResponse>> => {
    const user = useAppSelector((state) => state.user.user)
    const isGuestUser =
        useAppSelector((state) => state.searchParams.isGuestUser) === 'true'
    const guestUserPreferredUnit = useAppSelector(
        (state) => state.entityInstanceView.guestUserPreferredUnit,
    )
    return useQuery<IApacheChartAPIResponse>({
        queryKey: [
            'ApacheEChart',
            viewID,
            selectedAttributes,
            startDate,
            endDate,
            orgId,
            user,
            guestUserPreferredUnit,
            clampToRelationStart,
        ],
        queryFn: async (): Promise<IApacheChartAPIResponse> => {
            if (!orgId || selectedAttributes?.length === 0) {
                return {
                    data: { datasets: [], labels: [] },
                    mapData: [],
                    details: {
                        columns: [],
                        data: [],
                        totalRowCount: 0,
                    },
                }
            }
            // Add 1 second to prevent millisecond truncation issues with the API.
            const updatedEndDate = dayjs(endDate)
                .add(1, 'second')
                .format(ISO_DATE_TIME_FORMAT)
            // Must return Promise<IChartAPIResponse>
            return fetchApacheEChartData(
                viewID,
                selectedAttributes,
                startDate,
                updatedEndDate,
                orgId,
                isGuestUser,
                guestUserPreferredUnit,
                clampToRelationStart,
            )
        },
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        staleTime: 0, // Data never changes unless start date, end date or selected attributes change
        gcTime: 1000 * 60 * 60, // 1 hour
        enabled: !!selectedAttributes && !!startDate && !!endDate,
    })
}
